import { DepreciationProfileCode } from '@api/models'
import { DeprecClassHookProps } from '@components/depreciation-class/DepreciationClass.types'
import { useDepreciationClass } from '@hooks'
import { ASSET_PATHS } from '@constants'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetAssetDepreciationQuery, useGetCompanyQuery } from '@api'

interface IAssetDepreciationProfilesHook {
  deprecClassHookProps: DeprecClassHookProps
  depreciationProfileLabel: string
  isLoading: boolean
  isProfileMaintained: boolean
  hasUnitsOfProduction: boolean
  sideNavItems: {
    label: string
    onClick
    isActive: boolean
  }[]
}

export default function useAssetDepreciationProfiles(): IAssetDepreciationProfilesHook {
  const params = useParams()
  const navigate = useNavigate()

  const profile = params.profile as DepreciationProfileCode
  const assetID = params.assetID
  const assetCompanyID = parseInt(params.companyID)

  const { data: company } = useGetCompanyQuery(assetCompanyID, {})

  const profilesConfig = [
    { code: 'A' as DepreciationProfileCode, maintained: true }, // Corporate always maintained
    { code: 'X' as DepreciationProfileCode, maintained: company?.CM_TAX ?? false }, // Tax
    { code: 'S' as DepreciationProfileCode, maintained: company?.CM_SMH ?? false }, // State
    { code: 'O' as DepreciationProfileCode, maintained: company?.CM_B4MH ?? false }, // Other
  ]

  const profileA = useGetAssetDepreciationQuery(
    { assetCompanyID, assetID, profile: 'A' },
    { skip: !profilesConfig[0].maintained }
  )
  const profileX = useGetAssetDepreciationQuery(
    { assetCompanyID, assetID, profile: 'X' },
    { skip: !profilesConfig[1].maintained }
  )
  const profileS = useGetAssetDepreciationQuery(
    { assetCompanyID, assetID, profile: 'S' },
    { skip: !profilesConfig[2].maintained }
  )
  const profileO = useGetAssetDepreciationQuery(
    { assetCompanyID, assetID, profile: 'O' },
    { skip: !profilesConfig[3].maintained }
  )

  const hasUnitsOfProduction = [
    profilesConfig[0].maintained ? profileA.data?.DP_MET_CD === 'P' : false,
    profilesConfig[1].maintained ? profileX.data?.DP_MET_CD === 'P' : false,
    profilesConfig[2].maintained ? profileS.data?.DP_MET_CD === 'P' : false,
    profilesConfig[3].maintained ? profileO.data?.DP_MET_CD === 'P' : false,
  ].some(hasP => hasP)

  // Hook into useDepreciationClass
  const deprecClassHookProps: DeprecClassHookProps = {
    context: 'asset',
    params: {
      assetCompanyID,
      assetID,
      profile,
    },
  }

  const { isLoading, isProfileMaintained } = useDepreciationClass(deprecClassHookProps)

  // Nav
  const sideNavItems = [
    {
      label: 'Corporate',
      onClick: () => navigate(`../${ASSET_PATHS.DEPRECIATION}/A`),
      isActive: profile === 'A',
    },
    {
      label: 'Tax',
      onClick: () => navigate(`../${ASSET_PATHS.DEPRECIATION}/X`),
      isActive: profile === 'X',
    },
    {
      label: 'State',
      onClick: () => navigate(`../${ASSET_PATHS.DEPRECIATION}/S`),
      isActive: profile === 'S',
    },
    {
      label: 'Other',
      onClick: () => navigate(`../${ASSET_PATHS.DEPRECIATION}/O`),
      isActive: profile === 'O',
    },
  ]

  const depreciationProfileLabel: string = sideNavItems.find(item => item.isActive)?.label

  return {
    deprecClassHookProps,
    depreciationProfileLabel,
    isLoading,
    isProfileMaintained,
    hasUnitsOfProduction,
    sideNavItems,
  }
}
